import { useMemo } from 'react'
import { SearchableSelect, type SearchOption } from '@/components/SearchableSelect'
import { COST_TYPES, COST_TYPE_LABELS } from '@/lib/constants'
import type { Material } from '@/lib/types'

/**
 * Picks a cost item (material, labor, equipment, logistics) from the catalog: a search box over the
 * name, its translations, the code and the category; the list is split by cost type. An id the
 * catalog does not know stays selected as «… (пункт не найден)» until another is picked.
 */
export function MaterialPicker({
  materials,
  selectedId,
  onSelect,
  ariaLabel,
}: {
  materials: readonly Material[]
  /** `''` = none. */
  selectedId: string
  onSelect: (id: string) => void
  ariaLabel?: string
}) {
  const options = useMemo<SearchOption[]>(
    () =>
      COST_TYPES.flatMap((type) =>
        materials
          .filter((item) => item.costType === type)
          .map((item) => ({
            id: item.id,
            label: item.unit ? `${item.name} (${item.unit})` : item.name,
            detail: item.category || undefined,
            group: COST_TYPE_LABELS[type],
            search: [item.name, item.code, item.category, ...Object.values(item.nameI18n)].join(' '),
          })),
      ),
    [materials],
  )
  const selected = selectedId ? materials.find((item) => item.id === selectedId) : undefined
  const buttonLabel = selected
    ? selected.unit
      ? `${selected.name} (${selected.unit})`
      : selected.name
    : selectedId
      ? `${selectedId} (пункт не найден)`
      : '— пункт не выбран —'
  return (
    <SearchableSelect
      options={options}
      noneLabel="— пункт не выбран —"
      selectedId={selected ? selected.id : selectedId ? null : ''}
      buttonLabel={buttonLabel}
      onSelect={onSelect}
      ariaLabel={ariaLabel}
      searchPlaceholder="Поиск пункта затрат"
      listLabel="Пункты затрат"
    />
  )
}
